import { Calendar, User, Tag, ArrowLeft, Clock, Share2, Phone, Mail, TrendingUp, Target, Users, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function CareerProgression() {
  const shareUrl = window.location.href
  const shareTitle = "Building a Long-Term Career in Logistics"

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    }
  }

  const careerPath = [
    {
      level: "Entry Level",
      roles: ["HGV Driver (Class 2)", "Delivery Driver", "Warehouse Operative"],
      salary: "£24,000 - £28,000",
      skills: ["Valid HGV licence", "Clean driving record", "Basic safety awareness"],
      duration: "0-2 years"
    },
    {
      level: "Experienced Driver",
      roles: ["HGV Driver (Class 1)", "Specialist Transport", "ADR Driver"],
      salary: "£30,000 - £38,000",
      skills: ["Multiple endorsements", "Excellent safety record", "Customer service"],
      duration: "2-5 years"
    },
    {
      level: "Senior Driver/Trainer",
      roles: ["Driver Trainer", "Lead Driver", "Owner Operator"],
      salary: "£35,000 - £45,000",
      skills: ["Training qualifications", "Leadership", "Business acumen"],
      duration: "5-10 years"
    },
    {
      level: "Management",
      roles: ["Transport Manager", "Operations Manager", "Depot Manager"],
      salary: "£40,000 - £60,000",
      skills: ["CPC qualification", "Team management", "Commercial awareness"],
      duration: "10+ years"
    },
    {
      level: "Senior Management",
      roles: ["Regional Manager", "Operations Director", "Managing Director"],
      salary: "£55,000 - £100,000+",
      skills: ["Strategic planning", "P&L responsibility", "Industry expertise"],
      duration: "15+ years"
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white py-16">
        <div className="container-full">
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/blog" 
              className="flex items-center gap-2 text-c9-orange hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>4th June 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>10 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Sam Wharfe (CEO, C9 Recruitment)</span>
              </div>
              <span className="bg-c9-orange px-3 py-1 rounded-full text-xs font-medium">Career Advice</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              Building a Long-Term <span className="text-c9-orange">Career</span> in Logistics
            </h1>
            
            <p className="text-xl mb-8 leading-relaxed opacity-90">
              From entry-level positions to management roles - mapping your strategic career progression in transport and logistics. Expert guidance for professional drivers seeking long-term career growth.
            </p>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Share Article
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container-full">
          <div className="article-content">
            <div className="article-prose">
              
              <h2 className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-c9-orange" />
                Career Progression Roadmap
              </h2>
              
              <p>The logistics industry offers exceptional opportunities for career advancement. With the right strategy, dedication, and professional guidance, you can progress from entry-level positions to senior management roles, significantly increasing your earning potential and job satisfaction along the way.</p>

              <div className="article-highlight">
                <div className="flex items-start gap-3">
                  <Target className="h-6 w-6 text-c9-orange mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-c9-blue mb-2">Career Success Insight</h3>
                    <p className="text-gray-700">Successful logistics careers are built on a foundation of continuous learning, professional development, and strategic career planning. The industry rewards those who invest in their skills and actively pursue growth opportunities.</p>
                  </div>
                </div>
              </div>

              <h2>Understanding Career Progression Levels</h2>
              
              <p>The logistics sector offers a clear progression pathway, with each level building upon the previous one. Understanding these levels helps you plan your career journey effectively:</p>

              <div className="space-y-6 my-8">
                {careerPath.map((level, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-c9-blue">{level.level}</h3>
                      <span className="text-sm text-gray-500">{level.duration}</span>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Typical Roles</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {level.roles.map((role, roleIndex) => (
                            <li key={roleIndex}>• {role}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Key Skills</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {level.skills.map((skill, skillIndex) => (
                            <li key={skillIndex}>• {skill}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Salary Range</h4>
                        <p className="text-lg font-semibold text-c9-orange">{level.salary}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h2>Essential Skills for Career Advancement</h2>
              
              <h3>Technical Competencies</h3>
              <ul>
                <li><strong>Professional Qualifications:</strong> CPC, ADR, DCPC certifications</li>
                <li><strong>Technology Proficiency:</strong> Transport management systems, digital tachographs</li>
                <li><strong>Compliance Knowledge:</strong> Regulations, safety standards, working time directives</li>
                <li><strong>Operational Excellence:</strong> Route optimization, load planning, fuel efficiency</li>
                <li><strong>Customer Service:</strong> Communication skills, problem-solving abilities</li>
              </ul>

              <h3>Leadership and Management Skills</h3>
              <ul>
                <li><strong>Team Leadership:</strong> Motivating and managing driver teams</li>
                <li><strong>Performance Management:</strong> KPI monitoring and improvement</li>
                <li><strong>Commercial Awareness:</strong> Understanding P&L, cost control</li>
                <li><strong>Strategic Planning:</strong> Long-term operational planning</li>
                <li><strong>Change Management:</strong> Leading operational improvements</li>
              </ul>

              <h2>Strategic Career Development</h2>
              
              <h3>Professional Development Opportunities</h3>
              <p>Investing in continuous learning is crucial for career advancement:</p>
              
              <ul>
                <li><strong>Industry Certifications:</strong> CPC qualification for management roles</li>
                <li><strong>Professional Bodies:</strong> Join the Chartered Institute of Logistics and Transport (CILT)</li>
                <li><strong>Training Courses:</strong> Leadership development, health and safety training</li>
                <li><strong>Higher Education:</strong> Logistics and supply chain management degrees</li>
                <li><strong>Mentorship Programs:</strong> Learning from experienced industry professionals</li>
              </ul>

              <div className="article-warning">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-yellow-800 mb-2">Industry Evolution</h3>
                    <p className="text-yellow-700">The logistics industry is rapidly evolving with new technologies, environmental regulations, and changing customer expectations. Stay ahead by embracing innovation and developing future-focused skills.</p>
                  </div>
                </div>
              </div>

              <h3>Networking and Industry Connections</h3>
              <p>Building professional relationships is essential for career growth:</p>
              
              <ul>
                <li><strong>Industry Events:</strong> Attend logistics conferences and trade shows</li>
                <li><strong>Professional Networks:</strong> LinkedIn connections with industry leaders</li>
                <li><strong>Trade Associations:</strong> Active participation in industry organizations</li>
                <li><strong>Peer Groups:</strong> Connect with professionals at similar career stages</li>
                <li><strong>Supplier Relationships:</strong> Build connections across the supply chain</li>
              </ul>

              <h2>Specialization Opportunities</h2>
              
              <p>Consider developing expertise in high-growth logistics sectors:</p>
              
              <ul>
                <li><strong>Green logistics:</strong> Sustainable transport and environmental compliance</li>
                <li><strong>E-commerce fulfillment:</strong> Last-mile delivery optimization</li>
                <li><strong>Dangerous goods:</strong> ADR specialization for hazardous materials</li>
                <li><strong>International transport:</strong> Cross-border logistics and customs</li>
                <li><strong>Technology integration:</strong> Digital transformation and automation</li>
                <li><strong>Supply chain management:</strong> End-to-end logistics optimization</li>
              </ul>

              <div className="article-success">
                <div className="flex items-start gap-3">
                  <Users className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-green-800 mb-2">Success Strategy</h3>
                    <p className="text-green-700">The most successful logistics professionals combine technical expertise with strong people skills. Invest in both operational knowledge and leadership development throughout your career.</p>
                  </div>
                </div>
              </div>

              <h2>Creating Your Career Action Plan</h2>
              
              <h3>Assessment and Goal Setting</h3>
              <ol>
                <li><strong>Self-assessment:</strong> Evaluate current skills, interests, and strengths</li>
                <li><strong>Research opportunities:</strong> Understand market demands and growth areas</li>
                <li><strong>Set SMART goals:</strong> Specific, measurable, achievable, relevant, time-bound</li>
                <li><strong>Identify skill gaps:</strong> What do you need to learn or develop?</li>
                <li><strong>Create timeline:</strong> Map out your progression over 5-10 years</li>
              </ol>

              <h3>Implementation Strategy</h3>
              <ul>
                <li><strong>Regular reviews:</strong> Assess progress quarterly</li>
                <li><strong>Seek feedback:</strong> From supervisors, colleagues, and mentors</li>
                <li><strong>Take calculated risks:</strong> New challenges accelerate growth</li>
                <li><strong>Build your brand:</strong> Develop a reputation for excellence</li>
                <li><strong>Stay flexible:</strong> Adapt as industry and personal circumstances change</li>
              </ul>

              <h2>Working with Recruitment Partners</h2>
              
              <p>Specialist recruitment agencies can be valuable partners in your career development:</p>
              
              <ul>
                <li>Market intelligence and salary benchmarking</li>
                <li>Access to exclusive opportunities</li>
                <li>Career coaching and interview preparation</li>
                <li>Industry networking and connections</li>
                <li>Ongoing career development support</li>
              </ul>
              
              <p>At C9 Recruitment, we're committed to supporting long-term career development. We work with professionals at all stages of their logistics careers, from new drivers to senior executives, helping them find opportunities that align with their career goals and aspirations.</p>

            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="bg-c9-blue text-white section-padding">
        <div className="container-full text-center">
          <div className="max-w-3xl mx-auto">
            <div className="article-cta">
              <h3 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                <TrendingUp className="h-6 w-6" />
                Advance Your Transport Career
              </h3>
              <p className="text-lg mb-6">
                Ready to take the next step in your logistics career? Our industry specialists can help you identify opportunities that align with your career goals and provide expert guidance on professional development.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/candidates" 
                  className="flex items-center justify-center gap-2 bg-white text-c9-orange hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                  <ExternalLink className="h-5 w-5" />
                  Explore Career Opportunities
                </Link>
                <a 
                  href="tel:02081524574" 
                  className="flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-c9-blue px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Speak to Career Specialists
                </a>
              </div>
              
              <div className="mt-8 text-sm text-gray-200">
                <p>Get personalised career advice from our transport industry experts</p>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <a href="tel:02081524574" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="h-4 w-4" />
                    0208 152 4574
                  </a>
                  <a href="mailto:commercial@c9-recruitment.com" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="h-4 w-4" />
                    commercial@c9-recruitment.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
